import { useState } from 'react';
import { Button, Input } from '@staff-app/ui';
import { ShieldCheck } from 'lucide-react';
import type { IntlAllRequest } from '@staff-app/shared-types';
import { useApproveIntlAllFulfilment, useIsRootAir, useRevokeIntlAllFulfilmentApproval } from '../hooks/useIntlAllRequests';
import { isPaid, isWrittenOff } from './ShipmentsPanel';

function fmt(d?: string | null): string {
  if (!d) return '';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '' : parsed.toLocaleString();
}

/**
 * ROOT ONLY (Paul, 2026-10-28): approve an invoiced, unpaid request to ship before payment, with a
 * reason. Fulfilment then sees the order buttons as if it were paid; the invoice is still chased,
 * and a written-off invoice still blocks shipping. Everyone sees an approval once it exists.
 */
export function FulfilApproval({ request }: { request: IntlAllRequest }) {
  const isRoot = useIsRootAir();
  const [approve, approveState] = useApproveIntlAllFulfilment();
  const [revoke, revokeState] = useRevokeIntlAllFulfilmentApproval();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const swallow = () => {
    // Surfaced from the mutation's `error` below.
  };
  const error = approveState.error ?? revokeState.error ?? null;

  if (request.fulfilApprovedAt) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/40" data-testid="air-fulfil-approved">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex gap-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">{isPaid(request) ? 'Was approved to ship before payment' : 'Approved to ship before payment'}</p>
              <p className="text-xs text-muted-foreground">
                {`${request.fulfilApprovedBy ?? ''} · ${fmt(request.fulfilApprovedAt)}`}
              </p>
              <p className="mt-1">{request.fulfilApprovalReason}</p>
            </div>
          </div>
          {isRoot && !isPaid(request) ? (
            <Button
              size="sm"
              variant="outline"
              disabled={revokeState.loading}
              onClick={() => revoke({ variables: { requestId: request.id } }).catch(swallow)}
              data-testid="air-fulfil-revoke"
            >
              {revokeState.loading ? 'Revoking…' : 'Revoke'}
            </Button>
          ) : null}
        </div>
        {error ? <p className="mt-2 text-xs text-destructive">{error.message}</p> : null}
      </div>
    );
  }

  if (!isRoot || isPaid(request) || isWrittenOff(request)) return null;

  // Only an OPEN invoice — sent to the customer, not yet paid — can be approved (Paul, 2026-10-28).
  // Before that the button shows greyed out, saying what it waits for.
  const invoiceOpen = request.invoiceStatus === 'open';
  if (!open || !invoiceOpen) {
    return (
      <div className="space-y-1">
        <Button
          size="sm"
          variant="destructive"
          disabled={!invoiceOpen}
          onClick={() => setOpen(true)}
          data-testid="air-fulfil-approve-open"
        >
          <ShieldCheck className="mr-2 h-4 w-4" />
          {'Approve shipping before payment'}
        </Button>
        {!invoiceOpen ? (
          <p className="text-xs text-muted-foreground" data-testid="air-fulfil-approve-waiting">
            {request.stripeInvoiceId
              ? 'Available once the invoice is sent (status open).'
              : 'Available once the invoice is raised and sent (status open).'}
          </p>
        ) : null}
      </div>
    );
  }

  const trimmed = reason.trim();
  return (
    <div className="space-y-2 rounded-md border p-3 text-sm" data-testid="air-fulfil-approve-form">
      <p className="text-xs text-muted-foreground">
        {'Fulfilment will be able to place the orders now. The invoice stays open and is still chased.'}
      </p>
      <Input
        placeholder="Why it may ship unpaid, e.g. reseller on NET30"
        aria-label="Reason"
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid="air-fulfil-reason"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          // Red, like its opener: this ships goods nobody has paid for (Paul, 2026-10-28).
          variant="destructive"
          disabled={!trimmed || approveState.loading}
          onClick={() =>
            approve({ variables: { input: { requestId: request.id, reason: trimmed } } })
              .then(() => setOpen(false))
              .catch(swallow)
          }
          data-testid="air-fulfil-approve"
        >
          {approveState.loading ? 'Approving…' : 'Approve'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {'Cancel'}
        </Button>
      </div>
      {error ? <p className="text-xs text-destructive">{error.message}</p> : null}
    </div>
  );
}
